import React from 'react';

import {QrcodeOutlined, SafetyCertificateOutlined, WarningOutlined} from '@ant-design/icons';
import {Tooltip, Typography} from 'antd';
import styled from 'styled-components';

import {isQrPayment} from '../lib/utils';

import type {ChargeFieldsFragment, SubscriptionFieldsFragment} from 'api/generated';
import {CardBrand, ChargeStatus, PaymentMethods} from 'api/generated';
import {colors} from 'lib/colors';
import {getRiskColor, getRiskDescription, toDisplayScore} from 'lib/fraudRisk';
import {
  cardBrandLabels,
  paymentMethodIcons,
  paymentMethodLabels,
  paymentMethodMinimalIcons,
  tokenizationMethodLabels,
  tokenizationMethodMinimalIcons
} from 'lib/labels';
import {paymentBrandIcons} from 'lib/paymentBrandIcons';

const {Text} = Typography;

type Props = {
  item: ChargeFieldsFragment | SubscriptionFieldsFragment;
  minimal?: boolean;
  listView?: boolean;
};

const Dots = styled.span`
  font-family: Helvetica, Arial, sans-serif;
  letter-spacing: 1px;
`;

const BrandPayment = styled.div`
  display: flex;
  align-items: Center;
  grid-column-gap: 8px;
`;

const BrandIcon = styled.img`
  width: 30px;
`;

const CardNumber = styled.div`
  min-width: 40px;
`;

const MethodIcon = styled.img``;

const Spaced = styled.div<{tokenizationMethodIcon: boolean; minimal: boolean}>`
  display: ${(props) => (props.tokenizationMethodIcon ? 'grid' : 'flex')};
  grid-template-columns: ${(props) => (props.minimal ? '28px 80px 32px' : '28px 120px 32px')};
  grid-gap: ${(props) => (props.tokenizationMethodIcon ? '0' : '8px')};
  align-items: center;
`;

export const PaymentMethodOverview: React.FC<Props> = ({item, minimal, listView = true}) => {
  const {paymentMethod} = item;
  if (!paymentMethod) {
    if (minimal) return null;
    return <Text type="secondary">No payment method</Text>;
  }
  const brand = paymentMethod.card?.brand || paymentMethod.cardPresent?.brand || CardBrand.UNKNOWN;
  const tokenizationMethod = paymentMethod.card?.tokenizationMethod;

  const paymentMethodIcon = paymentMethod.method
    ? (minimal ? paymentMethodMinimalIcons : paymentMethodIcons)[paymentMethod.method]
    : null;

  const tokenizationMethodIcon =
    tokenizationMethod && tokenizationMethodMinimalIcons[tokenizationMethod];

  return (
    <Spaced tokenizationMethodIcon={!!tokenizationMethodIcon || listView} minimal={!!minimal}>
      {paymentMethodIcon ? (
        <>
          {listView && <div></div>}
          <div>
            <MethodIcon
              src={paymentMethodIcon.src}
              title={paymentMethod.method ? paymentMethodLabels[paymentMethod.method] : ''}
              style={paymentMethodIcon.style}
            />
          </div>
        </>
      ) : (
        <>
          {(tokenizationMethodIcon || listView) && (
            <div>
              <MethodIcon
                src={tokenizationMethodIcon?.src}
                title={tokenizationMethod ? tokenizationMethodLabels[tokenizationMethod] : ''}
                style={{...tokenizationMethodIcon?.style}}
              />
            </div>
          )}
          <BrandPayment>
            <BrandIcon
              src={paymentBrandIcons[brand]}
              alt={brand}
              title={
                cardBrandLabels[
                  paymentMethod.card?.brand || paymentMethod.cardPresent?.brand || CardBrand.UNKNOWN
                ]
              }
            />
            <CardNumber className="card-number">
              {!minimal && <Dots>••••</Dots>}{' '}
              {paymentMethod.card?.last4 || paymentMethod.cardPresent?.last4}
            </CardNumber>
          </BrandPayment>
        </>
      )}
      <div>
        {item.__typename === 'Charge' && isQrPayment(item) && (
          <Tooltip title="QR Payment">
            <QrcodeOutlined />
          </Tooltip>
        )}
        {item.__typename === 'Charge' &&
          (() => {
            const score = item.fraudDetectorScore;
            const displayScore = typeof score === 'number' ? toDisplayScore(score) : null;
            const isNo3DS =
              item.status === ChargeStatus.SUCCEEDED &&
              !item.sequenceId &&
              paymentMethod.method === PaymentMethods.CARD &&
              !paymentMethod.card?.threeDSecure &&
              !paymentMethod.card?.tokenizationMethod;
            const no3DSMessage = 'This transaction was processed without 3D Secure authentication.';

            if (displayScore !== null) {
              const color = getRiskColor(displayScore);
              const tooltip = isNo3DS
                ? `${getRiskDescription(displayScore)} ${no3DSMessage}`
                : getRiskDescription(displayScore);
              return (
                <Tooltip title={tooltip}>
                  <SafetyCertificateOutlined style={{color}} />
                </Tooltip>
              );
            }

            if (isNo3DS) {
              return (
                <Tooltip title={no3DSMessage}>
                  <WarningOutlined style={{color: colors.warning}} />
                </Tooltip>
              );
            }

            return null;
          })()}
      </div>
    </Spaced>
  );
};
